import type { FigmaGlyph } from 'aos-ds/src/icons/glyph'
import { Icon } from 'aos-ds/src/icons/Icon'
import { ChevronDown, ChevronUp } from 'aos-ds/src/icons/navigation'
import { Typography } from 'aos-ds/src/typography/Typography'
import React from 'react'
import { View } from 'react-native'

import { twMerge } from '../../tailwind/merge'
import {
    LABEL_VARIANT,
    resolveCollapsed,
    resolveFilter,
    resolveTrigger,
} from './filterSwitchContracts'
import type { FilterSwitchGroup, SwitchProps } from './types'

const CHEVRONS: Record<string, FigmaGlyph> = { ChevronDown, ChevronUp }

const resolveChevron = (open: boolean): FigmaGlyph => {
    const name = resolveTrigger(open).composes.find(item => item.component in CHEVRONS)?.component
    const glyph = name ? CHEVRONS[name] : undefined
    if (!glyph) {
        throw new Error(`FilterSwitchRow: FilterSwitch/Trigger open=${open} composes no chevron`)
    }
    return glyph
}

const TRIGGER_PILL_PART = 'Filter'

const triggerPillClass = (open: boolean): string => {
    const part = resolveTrigger(open).parts[TRIGGER_PILL_PART]
    if (!part) {
        throw new Error(`FilterSwitchRow: FilterSwitch/Trigger has no "${TRIGGER_PILL_PART}" part`)
    }
    return part
}

const TRIGGER_TRACK_PART = 'FilterSwitch/Trigger'

const HOVER_GROUP = /^group\//

// The dumper lifted the inner pill's stroke onto the trigger cell; drawing it doubles the ring.
const triggerTrackClass = (open: boolean): string => {
    const track = resolveCollapsed(open).parts[TRIGGER_TRACK_PART]
    if (!track) {
        throw new Error(
            `FilterSwitchRow: FilterSwitch/Collapsed open=${open} has no "${TRIGGER_TRACK_PART}"`,
        )
    }
    const hoverGroup = resolveTrigger(open)
        .container.split(' ')
        .filter(item => HOVER_GROUP.test(item))
    if (hoverGroup.length !== 1) {
        throw new Error(`FilterSwitchRow: FilterSwitch/Trigger open=${open} names no hover group`)
    }
    return [...hoverGroup, track].join(' ')
}

const labelOf = (group: FilterSwitchGroup): string =>
    group.options.find(option => option.value === group.value)?.label ?? ''

type TriggerProps = SwitchProps & { open: boolean }

/**
 * The pill sizes to its widest option, so changing the selection never resizes
 * it: every label sits in a zero-height column under the visible one, and the
 * column is as wide as the widest.
 */
export const FilterSwitchTrigger = ({ group, open, testID }: TriggerProps) => {
    const trigger = resolveTrigger(open)
    const label = resolveFilter('selected', open ? 'dropdown-open' : 'dropdown-closed').label
    return (
        <View className={twMerge('flex-row', triggerTrackClass(open))} testID={testID}>
            <View
                className={twMerge('flex-row flex-1', triggerPillClass(open))}
                testID={`${testID}-pill`}
            >
                <View className='flex-1'>
                    <Typography
                        variant={LABEL_VARIANT}
                        className={label}
                        testID={`${testID}-label`}
                    >
                        {labelOf(group)}
                    </Typography>
                    <View className='h-0 overflow-hidden' aria-hidden>
                        {group.options.map(option => (
                            <Typography
                                key={String(option.value)}
                                variant={LABEL_VARIANT}
                                className={label}
                            >
                                {option.label}
                            </Typography>
                        ))}
                    </View>
                </View>
                <Icon glyph={resolveChevron(open)} className={trigger.icon} />
            </View>
        </View>
    )
}
